/**
 * useAdmin Hook
 * Custom hook for admin functionality including queue monitoring and job management.
 * Based on backend API_ROUTES_FINAL.md specification.
 * 
 * All variable names, logs, and comments are in English.
 */

import { useApi } from './useApi';
import { adminApi } from '../api';
import type { 
  QueueStatsResponse, 
  JobsListResponse, 
  DeleteJobResponse, 
  CleanupResponse 
} from '../../types/api';

export function useAdmin() {
  // Queue statistics
  const { 
    loading: statsLoading, 
    error: statsError, 
    data: statsData, 
    trigger: fetchStats 
  } = useApi<QueueStatsResponse, void>(
    async () => {
      return await adminApi.getQueueStats();
    }
  );

  // Jobs list
  const { 
    loading: jobsLoading, 
    error: jobsError, 
    data: jobsData, 
    trigger: fetchJobs 
  } = useApi<JobsListResponse, { state?: string; limit?: number; offset?: number }>(
    async ({ state = 'all', limit = 50, offset = 0 }) => {
      return await adminApi.getJobs(state, limit, offset);
    }
  );

  // Delete job
  const { 
    loading: deleteLoading, 
    error: deleteError, 
    data: deleteData, 
    trigger: deleteJob 
  } = useApi<DeleteJobResponse, string>(
    async (jobId) => {
      return await adminApi.deleteJob(jobId);
    }
  );

  // Cleanup jobs
  const { 
    loading: cleanupLoading, 
    error: cleanupError, 
    data: cleanupData, 
    trigger: cleanupJobs 
  } = useApi<CleanupResponse, number>(
    async (maxAge) => {
      return await adminApi.cleanupJobs(maxAge);
    }
  );

  return {
    // Queue statistics
    statsLoading,
    statsError,
    queueStats: statsData,
    fetchQueueStats: fetchStats,

    // Jobs management
    jobsLoading,
    jobsError,
    jobsList: jobsData,
    fetchJobs,

    // Delete job
    deleteLoading,
    deleteError,
    deleteResult: deleteData,
    deleteJob,

    // Cleanup
    cleanupLoading,
    cleanupError,
    cleanupResult: cleanupData,
    cleanupJobs
  };
}

export default useAdmin;
